import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { School, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function ClassFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({ name: '', displayName: '', gradeNumber: 6 });

  const { data: existingData, isLoading: loadingExisting } = useQuery({
    queryKey: ['class', id],
    queryFn: async () => api.get<any>(`/api/classes/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingData?.data) {
      const cls = existingData.data;
      setFormData({
        name: cls.name || '',
        displayName: cls.display_name || '',
        gradeNumber: cls.grade_number || 6,
      });
    }
  }, [existingData]);

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      if (isEdit) return api.patch(`/api/classes/${id}`, payload);
      return api.post('/api/classes', payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Class updated' : 'Class created');
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      navigate('/classes');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save class');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  if (isEdit && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <School className="w-5 h-5 sm:w-6 sm:h-6 text-tangerine-500" />
            {isEdit ? 'Edit Class' : 'Add Class'}
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">
            {isEdit ? 'Update class details' : 'Create a new class'}
          </p>
        </div>
      </div>

      <div className="glass-card p-4 sm:p-6 rounded-2xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Class Name</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Class 6"
              required
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Display Name</Label>
            <Input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., VI Standard"
              className="h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Grade Number</Label>
            <Input
              type="number"
              min="6"
              max="12"
              value={formData.gradeNumber}
              onChange={(e) => setFormData({ ...formData, gradeNumber: parseInt(e.target.value) })}
              required
              className="h-10"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending} className="flex-1 h-10">
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isEdit ? 'Update' : 'Create'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
